import { z } from 'zod';
import { complianceRuleSchema } from './compliance.js';
import { hrRoleSchema } from '../schemas/auth.js';
import { fileCategorySchema } from '../files/categories.js';
import { triggerSchema } from './triggers.js';
import { gateSchema, jurisdictionSchema, requirementKindSchema, requirementOwnerSchema, requirementPhaseSchema, surfaceSchema, } from './vocabulary.js';
/**
 * RequirementRule — WHAT must be true about a person. Never HOW they satisfy it (that is
 * a StepDefinition, see link.ts) and never whether they currently do (that is derived).
 *
 * Split into three tiers for exactly the reason StepDefinitions are (definitions.ts): one
 * lineage cannot answer two questions.
 *
 *   policy       — compliance semantics. Changing it changes who is compliant, so it
 *                  VERSIONS and every version is retained for survey.
 *   presentation — the HR-facing label and wording. Configurable, edited in place,
 *                  carries no evidentiary weight (decision 8).
 *   metadata     — the legal/policy name and citations. Edited in place.
 *
 * NOTHING here is hashed. A hash attests "this person read these words"; nobody ever
 * signs a requirement, so a digest would attest to nothing while implying that
 * relabelling is dangerous.
 */
/**
 * kebab-case, and it IS the document id (`requirement:<key>`). Renaming is therefore not
 * merely forbidden by policy — it is structurally a different document, and no API path
 * can produce one. That guarantee is stronger than a branded type could give, and it
 * matters because this key is the join key for CredentialItems, StepInstances and
 * VerificationEvents.
 */
export const requirementKeySchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export function requirementDocId(key) {
    return `requirement:${key}`;
}
export function requirementVersionDocId(key, version) {
    return `requirement:${key}:v${version}`;
}
/**
 * Expiry as data: the nightly scan (PLAN §8) switches on `mode`, never on a requirement
 * key, so a new interval is a config edit rather than a deploy.
 */
export const calendarMonthExpirySchema = z.object({
    mode: z.literal('calendar-months'),
    everyMonths: z.number().int().min(1).max(120),
    anchor: z.enum(['last-completion', 'hire-date', 'anniversary']).default('last-completion'),
    /** Same-day clamps to the target month's last day; calendar-period ends the covered month. */
    alignment: z.enum(['same-day', 'calendar-period']).default('same-day'),
    graceDays: z.number().int().nonnegative().default(0),
    reminderTierDays: z.array(z.number().int().positive()).default([60, 30, 14, 7, 1]),
});
export const expiryRuleSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('none') }),
    z.object({
        /** The credential carries its own date (a licence, a CPR card). */
        mode: z.literal('credential-date'),
        graceDays: z.number().int().nonnegative().default(0),
        reminderTierDays: z.array(z.number().int().positive()).default([60, 30, 14, 7, 1]),
    }),
    z.object({
        mode: z.literal('interval'),
        everyDays: z.number().int().positive(),
        anchor: z.enum(['last-completion', 'hire-date', 'anniversary']).default('last-completion'),
        graceDays: z.number().int().nonnegative().default(0),
        reminderTierDays: z.array(z.number().int().positive()).default([60, 30, 14, 7, 1]),
    }),
    z.object({
        /** Influenza season and anything else anchored to a calendar window. */
        mode: z.literal('seasonal'),
        seasonStartMonthDay: z.string().regex(/^\d{2}-\d{2}$/),
        seasonEndMonthDay: z.string().regex(/^\d{2}-\d{2}$/),
        reminderTierDays: z.array(z.number().int().positive()).default([30, 14, 7]),
    }),
    calendarMonthExpirySchema,
    z.object({
        /** hours-accumulator: N units per rolling window. */
        mode: z.literal('rolling-window'),
        windowDays: z.number().int().positive(),
        target: z.number().positive(),
        unit: z.enum(['hours', 'occurrences']).default('hours'),
        paceWarningRatio: z.number().min(0).max(1).default(0.5),
    }),
    z.object({
        /** Open until the triggering event resolves. Never red, never overdue. */
        mode: z.literal('event-driven'),
    }),
    z.object({
        /**
         * The catalog has not settled this interval yet — roughly sixty rows are here on day
         * one (ADR-006 §5). Yields an open item with NO due date that the dashboard holds
         * without flagging red. Confident wrong due dates on real people destroy trust faster
         * than blank ones, so the engine refuses to invent one and names the open question
         * instead.
         */
        mode: z.literal('unresolved'),
        openQuestionId: z.string().min(1),
    }),
]);
/**
 * ORTHOGONAL PROPERTY 1 — blocking.
 *
 * A list of gates, not a boolean, because "blocking" is always blocking *of something*.
 * Empty means never blocking, which is the whole of decision 6 for agency and process
 * items — and it stays true no matter what `visibility` says.
 */
export const blockingRuleSchema = z.object({
    gates: z.array(gateSchema).default([]),
    /** Feeds "Ready with exceptions" (decision 9) rather than a silent bypass. */
    waivable: z.boolean().default(false),
    waiverRequiresRole: hrRoleSchema.nullable().default('HR_Admin'),
    waiverMaxDays: z.number().int().positive().nullable().default(null),
});
/**
 * ORTHOGONAL PROPERTY 2 — visibility.
 *
 * A set of surfaces, which is what makes decision 6 expressible: an agency item is on
 * `person-checklist` with `gates: []`. Visibility and blocking never read each other.
 *
 * `countsTowardProgress` is separate again, because "shown" and "counted" are also not
 * the same question — an agency-owned row on a nurse's checklist must not drag her
 * progress percentage down.
 */
export const visibilityRuleSchema = z.object({
    surfaces: z
        .array(surfaceSchema)
        .default(['person-checklist', 'hr-checklist', 'reports', 'audit-export']),
    countsTowardProgress: z.boolean().default(true),
    /** Health and I-9 rows: the person sees the row, an unprivileged viewer does not. */
    restrictedToRole: hrRoleSchema.nullable().default(null),
});
/** How completion is computed once the satisfying steps are known (see link.ts). */
export const satisfactionRuleSchema = z.object({
    mode: z.enum(['all-steps', 'any-step', 'hr-recorded']).default('all-steps'),
    derivedFrom: z
        .enum(['step-instances', 'credential-items', 'competency-records', 'verification-events'])
        .default('step-instances'),
});
/** The VERSIONED tier: everything that changes who is compliant. */
export const requirementPolicySchema = z.object({
    compliance: complianceRuleSchema.default({}),
    kind: requirementKindSchema,
    phase: requirementPhaseSchema,
    owner: requirementOwnerSchema,
    expiry: expiryRuleSchema,
    blocking: blockingRuleSchema,
    visibility: visibilityRuleSchema,
    /** ORTHOGONAL PROPERTY 3 — when the requirement applies at all. */
    trigger: triggerSchema,
    satisfaction: satisfactionRuleSchema,
    /**
     * A genuine FOURTH axis, not a flavour of trigger: bloodborne pathogens before the
     * hepatitis B offer makes an item *not yet actionable*, which is neither hidden nor
     * inapplicable.
     */
    prerequisites: z.array(requirementKeySchema).default([]),
    /**
     * Decision 2. Empty means "everywhere" — most rows are federal or accreditor-driven.
     * A state-specific licence lists its state, and is only in scope where the agency
     * operates. Adding a second state is then data entry on agency settings.
     */
    jurisdictions: z.array(jurisdictionSchema).default([]),
});
/** IN-PLACE tier: configurable HR-facing naming (decision 8). Never versioned, never hashed. */
export const requirementPresentationSchema = z.object({
    label: z.string().min(1),
    shortLabel: z.string().default(''),
    personDescription: z.string().default(''),
    hrNote: z.string().default(''),
});
/**
 * Before ADR-009 the category was called `sharePointTarget`, and requirement documents written
 * then carry that key. Read it as `fileCategory` so nothing already stored fails to load; the
 * old key is dropped here and never written again. When both are present the new one wins.
 */
function acceptLegacyFileCategoryKey(value) {
    if (typeof value !== 'object' || value === null || !('sharePointTarget' in value))
        return value;
    const { sharePointTarget, ...rest } = value;
    return 'fileCategory' in rest ? rest : { ...rest, fileCategory: sharePointTarget };
}
/** IN-PLACE tier: the legal and policy identity, kept as metadata (decision 8). */
export const requirementMetadataSchema = z.preprocess(acceptLegacyFileCategoryKey, z.object({
    legalName: z.string().default(''),
    policyNames: z.array(z.string().min(1)).default([]),
    citations: z.array(z.string().min(1)).default([]),
    evidenceDescription: z.string().default(''),
    /**
     * What kind of record an approved file for this requirement is, which decides where it is
     * stored. A closed vocabulary — see files/categories.ts for why this stopped being free text.
     */
    fileCategory: fileCategorySchema,
    /**
     * NOTE: there is no `segregated` flag here. Whether a file is segregated is a property
     * of its CATEGORY, so it is derived with `isSegregatedCategory()`. Storing it beside
     * the category let the two disagree, and they did — three rows claimed segregation while
     * pointing at the training library.
     */
    catalogRow: z.string().default(''),
}));
export const requirementRuleVersionSchema = z.object({
    id: z.string().min(1),
    docType: z.literal('requirement-rule-version'),
    key: requirementKeySchema,
    version: z.number().int().positive(),
    policy: requirementPolicySchema,
    changeReason: z.string().min(1),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
});
/**
 * The mutable head. Holds a POINTER to the current policy, never a copy — structurally
 * identical to `stepDefinitionSchema`, and for the identical reason: head and version
 * live in different Cosmos partitions (the container is partitioned on /id), so they
 * cannot be written in one transactional batch. Writing the version first and then
 * bumping the pointer leaves an orphan version on a crash, which is harmless.
 * Denormalising the policy onto the head would instead leave a head whose policy
 * disagrees with its own version number, and nothing would ever detect it.
 *
 * Retirement is `retiredAt`, never deletion: past readiness and the audit export both
 * resolve rules by key.
 */
export const requirementRuleSchema = z.object({
    id: z.string().min(1),
    docType: z.literal('requirement-rule'),
    key: requirementKeySchema,
    currentVersion: z.number().int().positive(),
    presentation: requirementPresentationSchema,
    metadata: requirementMetadataSchema,
    retiredAt: z.string().datetime().nullable().default(null),
});
//# sourceMappingURL=requirements.js.map